import { inject } from '@angular/core';
import { API_BASE_PATH } from '@pool-league/frontend-shared-util-app-config';
import { HttpMethod } from '@pool-league/shared-util-http';
import {
  EndpointPathParameters,
  Operation,
} from '@pool-league/shared-data-api';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

type OmitPath<F extends CallableFunction> = F extends (
  path: string,
  ...rest: infer Rest
) => unknown
  ? Rest
  : never;

export type HttpRequester<O extends Operation<string | void, HttpMethod>> = {
  [method in Lowercase<O['method']>]: <Response>(
    ...args: OmitPath<HttpClient[Lowercase<O['method']>]>
  ) => Observable<Response>;
};

export abstract class PoolLeagueApiClient {
  readonly http = inject(HttpClient);
  readonly #basePath = inject(API_BASE_PATH);

  protected constructor() {
    new Error(`API base path should not end with a slash: ${this.#basePath}`);
  }

  protected buildRequest<
    PathParameters extends void,
    O extends Operation<PathParameters, HttpMethod>,
  >(operation: O): HttpRequester<O>;
  protected buildRequest<
    PathParameters extends string,
    O extends Operation<PathParameters, HttpMethod>,
  >(
    operation: O,
    params: EndpointPathParameters<PathParameters>,
  ): HttpRequester<O>;
  protected buildRequest<
    PathParameters extends string | void,
    O extends Operation<PathParameters, HttpMethod>,
  >(
    operation: O,
    params?: PathParameters extends string
      ? EndpointPathParameters<PathParameters>
      : never,
  ): HttpRequester<O> {
    const url = operation.path.reduce<string>((acc, nextPart) => {
      if (typeof nextPart === 'object') {
        const paramValue = params?.[nextPart.param];
        if (!paramValue) {
          throw new Error(`Missing path param value: ${nextPart.param}`);
        }
        return `${acc}/${encodeURIComponent(paramValue)}`;
      }
      return `${acc}/${nextPart}`;
    }, this.#basePath);

    const method = operation.method.toLowerCase() as Lowercase<HttpMethod>;
    return {
      [method]: this.http[method].bind(this.http, url),
    } as unknown as HttpRequester<O>;
  }
}
